import * as z from 'zod';

import { AuditLogId, EventId, LoanId, SideEffectId } from '../BrandedIds';
import { Actor } from '../events';
import { LoanEvent } from '../events/LoanEvent';
import { zodDateOrString } from '../utils/Zod';
import { AuditLogHiddenData } from './AuditLogHiddenData';
import { AuditLogBulkAdditionType, AuditLogEventType, AuditLogSideEffectType } from './AuditLogType';

export const AuditLogSideEffectInput = z.object({
  loanId: LoanId,
  type: AuditLogSideEffectType,
  sideEffectId: SideEffectId,
  comment: z.string().optional(),
});
export type AuditLogSideEffectInput = z.infer<typeof AuditLogSideEffectInput>;

export const AuditLogEventInput = z.object({
  loanId: LoanId,
  type: AuditLogEventType,
  comment: z.string().optional(), // Optionally used for audit log only items
  adminViewComment: z.string().optional(), // Real super admin names used
});
export type AuditLogEventInput = z.infer<typeof AuditLogEventInput>;

export type AuditLogInput = AuditLogSideEffectInput | AuditLogEventInput;

export const AuditLogSideEffect = AuditLogSideEffectInput.extend({
  id: AuditLogId,
  hidden: z.boolean(),
  comment: z.string().optional(),
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type AuditLogSideEffect = z.infer<typeof AuditLogSideEffect>;

export const AuditLogEvent = AuditLogEventInput.extend({
  id: AuditLogId,
  eventId: EventId,
  hidden: z.boolean(),
  hiddenData: AuditLogHiddenData.optional(),
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type AuditLogEvent = z.infer<typeof AuditLogEvent>;

/** Bulk additions */
export const AuditLogBulkAdditionInput = z.object({
  loanId: LoanId,
  type: AuditLogBulkAdditionType,
  comment: z.string(),
  adminViewComment: z.string(), // Real super admin names used
  actor: Actor,
});
export type AuditLogBulkAdditionInput = z.infer<typeof AuditLogBulkAdditionInput>;

export const AuditLogBulkAddition = AuditLogBulkAdditionInput.extend({
  id: AuditLogId,
  hidden: z.boolean(),
  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type AuditLogBulkAddition = z.infer<typeof AuditLogBulkAddition>;

/** ----------------------------------- */
export const AuditLogItem = z.union([AuditLogEvent, AuditLogSideEffect, AuditLogBulkAddition]);
export type AuditLogItem = z.infer<typeof AuditLogItem>;

export type AuditLogEventWithActor = AuditLogEvent & { actor: Actor };
export type AuditLogItemWithActor = AuditLogEventWithActor | AuditLogSideEffect | AuditLogBulkAddition;

/** Hydrated includes the actual object of the event instead of just the id */
export type HydratedAuditLogEvent = AuditLogEvent & { event: LoanEvent };
export type HydratedAuditLogItem = HydratedAuditLogEvent | AuditLogSideEffect | AuditLogBulkAddition;
